// src/components/Loader.js
import React from 'react';
import styled from 'styled-components';

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.bg};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
  backdrop-filter: blur(10px);
`;

const LoaderWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  animation: floatUp 4s ease-in-out infinite;
  
  @keyframes floatUp {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const SpinnerContainer = styled.div`
  width: 80px;
  height: 80px;
  position: relative;
  margin: 20px 0;
  
  &::after {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    border: 2px solid rgba(133, 76, 230, 0.3);
    border-radius: 50%;
    animation: ripple 2.5s ease-out infinite;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -30px;
    left: -30px;
    right: -30px;
    bottom: -30px;
    border: 1px solid rgba(255, 107, 107, 0.2);
    border-radius: 50%;
    animation: ripple 3.5s ease-out infinite;
    animation-delay: 0.5s;
  }
  
  @keyframes ripple {
    0% {
      transform: scale(0.8);
      opacity: 0.8;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
`;

const Spinner = styled.div`
  width: 80px;
  height: 80px;
  border: 5px solid rgba(133, 76, 230, 0.1);
  border-top: 5px solid #854CE6;
  border-right: 5px solid rgba(133, 76, 230, 0.3);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
  position: relative;
  box-shadow: 0 0 20px rgba(133, 76, 230, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 4px solid rgba(255, 107, 107, 0.1);
    border-top: 4px solid #FF6B6B;
    border-left: 4px solid rgba(255, 107, 107, 0.3);
    border-radius: 50%;
    animation: spin 1.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite reverse;
    box-shadow: 0 0 15px rgba(255, 107, 107, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 12px;
    right: 12px;
    bottom: 12px;
    border: 3px solid rgba(157, 92, 232, 0.1);
    border-top: 3px solid #9d5ce8;
    border-bottom: 3px solid rgba(157, 92, 232, 0.3);
    border-radius: 50%;
    animation: spin 0.9s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
    box-shadow: 0 0 10px rgba(157, 92, 232, 0.3);
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 50%, #854CE6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  background-size: 200% 100%;
  animation: pulse 2s ease-in-out infinite, textShimmer 3s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 70% { opacity: 1; }
    85% { opacity: 0.6; }
    100% { opacity: 1; }
  }
  
  @keyframes textShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, #854CE6, #9d5ce8);
    animation: dotBounce 1.6s ease-in-out infinite;
    box-shadow: 0 4px 12px rgba(133, 76, 230, 0.4);
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border-radius: 50%;
      background: inherit;
      opacity: 0.3;
      animation: dotGlow 1.6s ease-in-out infinite;
    }
    
    &:nth-child(1) { 
      animation-delay: 0s;
      background: linear-gradient(135deg, #854CE6, #9d5ce8);
    }
    &:nth-child(2) { 
      animation-delay: 0.3s;
      background: linear-gradient(135deg, #FF6B6B, #ff8a8a);
      box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
    }
    &:nth-child(3) { 
      animation-delay: 0.6s;
      background: linear-gradient(135deg, #9d5ce8, #854CE6);
      box-shadow: 0 4px 12px rgba(157, 92, 232, 0.4);
    }
  }
  
  @keyframes dotBounce {
    0%, 70%, 100% {
      transform: translateY(0) scale(1);
      opacity: 0.8;
    }
    35% {
      transform: translateY(-20px) scale(1.3);
      opacity: 1;
    }
  }
  
  @keyframes dotGlow {
    0%, 70%, 100% {
      transform: scale(1);
      opacity: 0.3;
    }
    35% {
      transform: scale(1.8);
      opacity: 0;
    }
  }
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 4px;
  background: rgba(133, 76, 230, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 30px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    animation: shimmer 2s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-30px); }
    100% { transform: translateX(230px); }
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #854CE6, #FF6B6B, #9d5ce8);
  border-radius: 2px;
  animation: progressFill 3s ease-out infinite;
  position: relative;
  
  @keyframes progressFill {
    0% { width: 0%; }
    70% { width: 85%; }
    100% { width: 100%; }
  }
`;

const LoaderBackground = styled.div`
  background: radial-gradient(circle at 30% 50%, rgba(133, 76, 230, 0.08) 0%, transparent 60%),
              radial-gradient(circle at 70% 80%, rgba(255, 107, 107, 0.06) 0%, transparent 60%),
              radial-gradient(circle at 50% 20%, rgba(157, 92, 232, 0.04) 0%, transparent 40%);
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;

const Loader = () => {
    return (
        <LoaderOverlay>
            <LoaderBackground />
            <LoaderWrapper>
                <SpinnerContainer>
                    <Spinner />
                </SpinnerContainer>
                <LoadingText>Preparing Something Amazing...</LoadingText>
                <LoadingDots>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                </LoadingDots>
                <ProgressBar>
                    <ProgressFill />
                </ProgressBar>
            </LoaderWrapper>
        </LoaderOverlay>
    );
};

export default Loader;
